'use client';

import '@/app/model/stores/immer-config';
import { useEffect, useLayoutEffect } from 'react';
import { useLocale } from 'next-intl';
import { useAuthStore } from '@/features/auth';
import { useAuthEvents } from '@/features/auth/hooks/use-auth-events';
import { AuthTokenService } from '@/shared/lib/auth-token-service';
import { apiClient } from '@/shared/lib/api';
import { initFirebaseAnalytics } from '@/shared/lib/firebase';
import { startAttributionSync } from '@/shared/lib/acquisition-attribution';
import { setAnalyticsUser } from '@/shared/lib/analytics';
import { useEffectivePlan } from '@/features/subscription/model/subscription.selectors';
import { queryClient } from '@/shared/lib/query-client';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import type { User } from '@/types/user';
import {
  ANONYMOUS_SCOPE,
  initUserScopedStorage,
} from '@/shared/lib/user-scoped-storage';

// Wire user-scoped Zustand stores to identity changes once at module load —
// the Query cache for `cacheKeys.user` is the canonical owner of the User
// entity (Wave A3 + STATE_POLICY), so we subscribe to its cache events.
// Injecting accessors (rather than importing the auth store inside
// `user-scoped-storage`) keeps that module a leaf — see its header for the
// cycle it would otherwise close.
initUserScopedStorage({
  readScope: () =>
    queryClient.getQueryData<User>(cacheKeys.user)?.id ?? undefined,
  subscribeScope: (cb) => {
    let prev =
      queryClient.getQueryData<User>(cacheKeys.user)?.id ?? ANONYMOUS_SCOPE;
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      const key = event.query.queryKey;
      if (
        !Array.isArray(key) ||
        key.length !== cacheKeys.user.length ||
        key[0] !== cacheKeys.user[0]
      ) {
        return;
      }
      const next =
        queryClient.getQueryData<User>(cacheKeys.user)?.id ?? ANONYMOUS_SCOPE;
      if (next !== prev) {
        const prior = prev;
        prev = next;
        cb(next, prior);
      }
    });
    return unsubscribe;
  },
});

export function StoreInitializer() {
  const hydrateAuthFromStorage = useAuthStore(
    (state) => state.hydrateAuthFromStorage
  );
  const initializeAuth = useAuthStore((state) => state.initializeAuth);
  const isInitialized = useAuthStore((state) => state.isInitialized);
  const locale = useLocale();

  // Adopt the stored session BEFORE the browser paints. A layout effect, not a
  // plain one: by the time paint happens the auth gates have already seen the
  // real answer, so a returning user never sees the "checking..." spinner that
  // the network validation below used to keep on screen. The store's initial
  // state stays anonymous so the first client render still matches the
  // server's HTML — this flips it in the commit right after, before paint.
  useLayoutEffect(() => {
    hydrateAuthFromStorage();
  }, [hydrateAuthFromStorage]);

  // Initialize auth event listeners
  useAuthEvents();

  // Sync locale to API client so Accept-Language header is sent with every request
  useEffect(() => {
    apiClient.setLocale(locale);
  }, [locale]);

  // WebSocket connection is handled by auth store
  // Active renders are now synced via RENDER_QUEUE_UPDATED WebSocket event

  // Subscription data is now loaded with user profile in auth.store

  // Bootstrap Firebase / Google Analytics once on client mount. Skipped in
  // dev (set NEXT_PUBLIC_FIREBASE_ANALYTICS=on to override locally).
  useEffect(() => {
    void initFirebaseAnalytics();
  }, []);

  // Acquisition attribution: capture the landing cookie / ad click from the
  // URL and send it after any sign-in (see acquisition-attribution.ts).
  useEffect(() => {
    startAttributionSync();
  }, []);

  // Keep funnel-analytics user properties in sync with the plan tier + locale.
  // Passes are mobile-only, so they stay false here; `plan_type` is unset
  // because the web plan resource only carries `slug`/`name`.
  const effectivePlan = useEffectivePlan();
  useEffect(() => {
    setAnalyticsUser({
      planSlug: effectivePlan?.slug ?? null,
      planType: null,
      appLocale: locale,
    });
  }, [effectivePlan?.slug, locale]);

  // Step two: ask the server whether the adopted token is actually good, and
  // seed profile/subscription/balance caches from the answer. Runs in the
  // background — screens are already rendering off the hydrated session.
  useEffect(() => {
    // Initialize AuthTokenService first to emit initial token event
    AuthTokenService.initialize().then(() => {
      if (!isInitialized) {
        initializeAuth();
      }
    });
  }, [initializeAuth, isInitialized]);

  // WebSocket connection is now managed by auth store
  // When user logs in/out, auth store automatically calls connect/disconnect
  // The WebSocket service handles all channel subscriptions internally

  // This component doesn't render anything
  return null;
}
